export interface SeedTrialPlot {
  plot?: {
    id?: number;
    rows?: number;
    columns?: number;
    isStripPlot?: boolean;
  };
}

export interface SeedDesign {
  trial_plots?: SeedTrialPlot[];
  stripsData?: {
    isStripMode?: boolean;
    count?: number;
  } | null;
}

export interface SeedStripe {
  id: string;
  label: string;
  plotGroupId?: number;
}

export interface SeedProduct {
  id: string;
  name: string;
  color: string;
  min: number;
  max: number;
  densityOptions?: number[];
}

export interface StripeAssignment {
  productId: string;
  rate: number | null;
  color?: string;
  legacy?: boolean;
}

export type StripeAssignments = Record<string, StripeAssignment>;

export type SeedFlow = "plots" | "stripes";

export const resolveSeedFlow = (design: SeedDesign): SeedFlow => {
  const explicitMode = design.stripsData?.isStripMode;

  if (typeof explicitMode === "boolean") {
    return explicitMode ? "stripes" : "plots";
  }

  return design.trial_plots?.some(
    (trialPlot) => trialPlot.plot?.isStripPlot === true
  )
    ? "stripes"
    : "plots";
};

const isPositiveCount = (value: number | undefined): value is number =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

/**
 * Preserve existing Mapbox IDs.
 *
 * The hydrated display design preserves saved strip order, so its flattened
 * strip index maps to strip_design.strips[index]. It is not strip_id.
 */
export const buildSeedStripes = (design: SeedDesign): SeedStripe[] => {
  if (resolveSeedFlow(design) !== "stripes") {
    return [];
  }

  const cells = (design.trial_plots ?? []).flatMap(({ plot }): SeedStripe[] => {
    const groupId = plot?.id;
    const rows = plot?.rows;
    const columns = plot?.columns;

    if (
      groupId === undefined ||
      !isPositiveCount(rows) ||
      !isPositiveCount(columns)
    ) {
      return [];
    }

    return Array.from(
      { length: rows * columns },
      (_, index): SeedStripe => ({
        id: `${groupId}-${Math.floor(index / columns)}-${index % columns}`,
        label: "",
      })
    );
  });

  return cells.map((stripe, index) => ({
    ...stripe,
    plotGroupId: index,
    label: `Stripe ${index + 1}`,
  }));
};
